import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Typography, Box,
  Table, TableBody, TableCell, TableHead, TableRow, Button,
  Chip, CircularProgress, Alert, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import apiService from '../../services/apiService';
import { format } from 'date-fns';

const PendingUsersDialog = ({ open, onClose, onCountChange }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.get('/api/auth/pending-users');
      const list = res.data || [];
      setUsers(list);
      onCountChange(list.length);
    } catch (err) {
      setError('Failed to load pending requests.');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    if (open) fetchPending();
  }, [open, fetchPending]);

  const handleAction = async (userId, action) => {
    setActionLoading(userId + action);
    try {
      await apiService.authPut(`/api/auth/users/${userId}/${action}`);
      const updated = users.filter((u) => u._id !== userId);
      setUsers(updated);
      onCountChange(updated.length);
    } catch (err) {
      setError(`Failed to ${action} user.`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={600}>Pending Access Requests</Typography>
          {users.length > 0 && (
            <Chip label={users.length} size="small" color="warning" sx={{ fontWeight: 700 }} />
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : users.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">No pending access requests.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5' } }}>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u._id} hover>
                  <TableCell>{u.displayName}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{u.username}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{u.email}</TableCell>
                  <TableCell sx={{ maxWidth: 180, fontSize: '0.8rem', color: 'text.secondary' }}>
                    {u.signupReason || <em>—</em>}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    {u.createdAt ? format(new Date(u.createdAt), 'dd MMM yyyy') : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Tooltip title="Approve">
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={!!actionLoading}
                            onClick={() => handleAction(u._id, 'approve')}
                          >
                            {actionLoading === u._id + 'approve'
                              ? <CircularProgress size={16} />
                              : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!!actionLoading}
                            onClick={() => handleAction(u._id, 'reject')}
                          >
                            {actionLoading === u._id + 'reject'
                              ? <CircularProgress size={16} />
                              : <CancelIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PendingUsersDialog;
